/**
 * useTokenPricing Hook
 *
 * React hook for managing token pricing data from GeckoTerminal API
 * Features:
 * - Automatic price fetching for visible tokens
 * - Caching and rate limiting via PricingService
 * - Batch fetching optimization
 * - Live cross-tab synchronization via Dexie useLiveQuery
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { FormattedToken } from './utils';
import type { FormattedEvmToken } from './utils';
import { getPricingService, TokenPrice } from 'lib/pricing-service/pricing-service';
import { getPricingDB } from 'lib/pricing-service/pricing-db';
export interface UseTokenPricingOptions {
  /** Tokens to fetch prices for */
  tokens: (FormattedToken | FormattedEvmToken)[];
  /** Chain ID */
  chainId: number | string;
  /** Chain name for fallback network ID lookup */
  chainName?: string;
  /** Enable/disable fetching */
  enabled?: boolean;
  /** Auto-refresh interval in ms (0 = no auto-refresh) */
  refreshInterval?: number;
}
export interface UseTokenPricingReturn {
  /** Price data keyed by token address (lowercase) */
  prices: Map<string, TokenPrice>;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Manually refresh prices */
  refresh: () => Promise<void>;
  /** Service statistics */
  stats: ReturnType<typeof getPricingService>['getStats'];
}
export function useTokenPricing({
  tokens,
  chainId,
  chainName,
  enabled = true,
  refreshInterval = 0
}: UseTokenPricingOptions): UseTokenPricingReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Get pricing service and database instances
  const pricingService = getPricingService();
  const db = getPricingDB();
  const abortControllerRef = useRef<AbortController | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Token addresses to query (memoized)
  const tokenAddresses = useMemo(() => new Set(tokens.map(t => t.address.toLowerCase())), [tokens]);

  /**
   * Live query from IndexedDB - automatically updates across tabs!
   * This will re-run whenever the database changes, even from other tabs/windows
   */
  const cachedPrices = useLiveQuery(async () => {
    if (!enabled || tokenAddresses.size === 0) return [];

    // Query all tokens for this chain from IndexedDB
    const allTokensForChain = await db.tokens.where('chainId').equals(chainId).toArray();

    // Filter to only tokens we're interested in
    return allTokensForChain.filter(token => tokenAddresses.has(token.address.toLowerCase()));
  }, [chainId, tokenAddresses, enabled], [] // Default value while loading
  );

  /**
   * Convert cached prices to TokenPrice format and create Map
   */
  const prices = useMemo(() => {
    const priceMap = new Map<string, TokenPrice>();
    if (!cachedPrices) {
      return priceMap;
    }
    for (const cached of cachedPrices) {
      // Convert CachedTokenPrice to TokenPrice
      const tokenPrice: TokenPrice = {
        address: cached.address,
        symbol: cached.symbol,
        name: cached.name,
        decimals: cached.decimals,
        // Visual
        image_url: cached.imageUrl,
        // Pricing
        price_usd: cached.priceUsd,
        fdv_usd: cached.fdvUsd,
        market_cap_usd: cached.marketCapUsd,
        total_reserve_in_usd: cached.totalReserveInUsd,
        // Volume
        volume_usd_24h: cached.volumeUsd24h,
        // Supply
        total_supply: cached.totalSupply,
        normalized_total_supply: cached.normalizedTotalSupply,
        circulating_supply: cached.circulatingSupply,
        // CoinGecko
        coingecko_coin_id: cached.coingeckoCoinId,
        // Legacy fields
        price_change_24h: parseFloat(cached.pools[0]?.priceChangePercentage?.h24 || '0'),
        volume_24h: cached.volumeUsd24h || '0',
        market_cap: cached.marketCapUsd || '0',
        fdv: cached.fdvUsd,
        networks: [],
        // Pools
        pools: cached.pools,
        // Metadata
        timestamp: cached.timestamp
      };
      priceMap.set(cached.address.toLowerCase(), tokenPrice);
    }
    return priceMap;
  }, [cachedPrices]);

  /**
   * Fetch prices for tokens not in cache or force refresh
   * Updates will automatically appear via useLiveQuery
   */
  const fetchPrices = useCallback(async (forceRefresh = false) => {
    if (!enabled || tokens.length === 0) {
      return;
    }

    // Cancel any pending requests
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);
    try {
      // Extract addresses
      const addresses = tokens.map(t_0 => t_0.address);

      // Determine which addresses need fetching
      const addressesToFetch = forceRefresh ? addresses : addresses.filter(addr => !prices.has(addr.toLowerCase()));
      if (addressesToFetch.length === 0) {
        setIsLoading(false);
        return;
      }

      // Fetch prices - this will update IndexedDB
      // useLiveQuery will automatically pick up the changes
      await pricingService.getTokenPrices(addressesToFetch, chainId, {
        forceRefresh,
        chainName
      });
    } catch (err) {
      const error_0 = err instanceof Error ? err : new Error('Failed to fetch prices');
      console.error('[useTokenPricing] Error fetching prices:', error_0);
      setError(error_0);
    } finally {
      setIsLoading(false);
    }
  }, [tokens, chainId, enabled, pricingService, prices]);

  /**
   * Manual refresh
   */
  const refresh = useCallback(async () => {
    await fetchPrices(true);
  }, [fetchPrices]);

  /**
   * Get service stats
   */
  const getStats = useCallback(() => {
    return pricingService.getStats();
  }, [pricingService]);

  /**
   * Initial fetch and when dependencies change
   */
  useEffect(() => {
    fetchPrices(false);
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchPrices]);

  /**
   * Auto-refresh interval
   */
  useEffect(() => {
    if (refreshInterval > 0 && enabled) {
      refreshIntervalRef.current = setInterval(() => {
        fetchPrices(false);
      }, refreshInterval);
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [refreshInterval, enabled, fetchPrices]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      abortControllerRef.current?.abort();
    };
  }, []);
  return {
    prices,
    isLoading,
    error,
    refresh,
    stats: getStats
  };
}